import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { formatDisplayNumber } from './utils/helpers';
import './IncidentReportBoard.css';

const STATUS_ORDER = ['Open', 'Under Investigation', 'Resolved', 'Closed'];

const STATUS_COLORS = {
  'Open': '#dc2626',
  'Under Investigation': '#f59e0b',
  'Resolved': '#16a34a',
  'Closed': '#6b7280'
};

function IncidentReportBoard({ incidents, user, onEdit, onDelete, isAdmin }) {
  // Track which incident is being dragged + which column is being hovered.
  // Both are used purely for visual feedback; the actual move happens on drop.
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetStatus, setDropTargetStatus] = useState(null);

  const byStatus = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = incidents.filter(i => i.status === s);
    return acc;
  }, {});

  const handleStatusChange = async (incident, newStatus) => {
    if (newStatus === incident.status) return;
    try {
      await updateDoc(doc(db, 'incidents', incident.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Could not update status.');
    }
  };

  const canEdit = (incident) =>
    incident.createdBy === user.uid || isAdmin;

  // ── Drag-and-drop handlers ──────────────────────────────────────────────
  const handleDragStart = (e, incident) => {
    if (!canEdit(incident)) {
      e.preventDefault();
      return;
    }
    setDraggingId(incident.id);
    e.dataTransfer.setData('text/plain', incident.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTargetStatus(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault(); // required to allow drop
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetStatus !== status) setDropTargetStatus(status);
  };

  const handleDragLeave = (e, status) => {
    // Only clear if we're actually leaving the column, not just moving over
    // a child. relatedTarget can be null when leaving the window.
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dropTargetStatus === status) setDropTargetStatus(null);
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const incidentId = e.dataTransfer.getData('text/plain');
    setDropTargetStatus(null);
    setDraggingId(null);
    if (!incidentId) return;
    const incident = incidents.find(i => i.id === incidentId);
    if (!incident) return;
    if (!canEdit(incident)) return; // already guarded on dragstart, defense in depth
    if (incident.status === newStatus) return;
    await handleStatusChange(incident, newStatus);
  };

  const triggerBadges = (i) => {
    const badges = [];
    if (i.triggersInjury) badges.push({ key: 'inj', label: '🩹 Injury' });
    if (i.triggersSopViolation) badges.push({ key: 'sop', label: '📋 SOP' });
    if (i.triggersMaintenance) badges.push({ key: 'mnt', label: '🔧 Maint' });
    return badges;
  };

  return (
    <div className="incidents-board">
      {STATUS_ORDER.map(status => (
        <div
          key={status}
          className={`incidents-column ${dropTargetStatus === status ? 'incidents-column-drop-target' : ''}`}
          onDragOver={(e) => handleDragOver(e, status)}
          onDragLeave={(e) => handleDragLeave(e, status)}
          onDrop={(e) => handleDrop(e, status)}
        >
          <div className="incidents-column-header" style={{ borderTopColor: STATUS_COLORS[status] }}>
            <h3>{status}</h3>
            <span className="incidents-column-count">{byStatus[status].length}</span>
          </div>
          <div className="incidents-column-body">
            {byStatus[status].length === 0 && (
              <div className="incidents-empty">No incidents</div>
            )}
            {byStatus[status].map(incident => (
              <div
                key={incident.id}
                className={`incident-card ${draggingId === incident.id ? 'incident-card-dragging' : ''} ${!canEdit(incident) ? 'incident-card-readonly' : ''}`}
                draggable={canEdit(incident)}
                onDragStart={(e) => handleDragStart(e, incident)}
                onDragEnd={handleDragEnd}
              >
                <div className="incident-card-header">
                  {typeof incident.displayNumber === 'number' && (
                    <span className="incident-card-seq">
                      {formatDisplayNumber(incident, 'I')}
                    </span>
                  )}
                  <span className="incident-card-app">{incident.applicationName}</span>
                </div>
                <h4
                  className="incident-card-title incident-card-title-clickable"
                  onClick={() => onEdit(incident)}
                  title="Click to open"
                >
                  {incident.title}
                </h4>
                {incident.subject && (
                  <p className="incident-card-subject">{incident.subject}</p>
                )}
                <div className="incident-card-meta">
                  {incident.location && (
                    <span className="incident-meta-item">📍 {incident.location}</span>
                  )}
                  {incident.reportedBy && (
                    <span className="incident-meta-item">👤 {incident.reportedBy}</span>
                  )}
                  {incident.incidentDateTime && (
                    <span className="incident-meta-item">
                      🕐 {incident.incidentDateTime?.toDate?.().toLocaleString() ||
                          new Date(incident.incidentDateTime).toLocaleString()}
                    </span>
                  )}
                </div>
                {triggerBadges(incident).length > 0 && (
                  <div className="incident-card-triggers">
                    {triggerBadges(incident).map(b => (
                      <span key={b.key} className="trigger-badge">{b.label}</span>
                    ))}
                  </div>
                )}
                <div className="incident-card-footer">
                  <select
                    value={incident.status}
                    onChange={(e) => handleStatusChange(incident, e.target.value)}
                    disabled={!canEdit(incident)}
                    className="incident-status-select"
                  >
                    {STATUS_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div className="incident-card-actions">
                    <button
                      onClick={() => onEdit(incident)}
                      className="btn-icon"
                      title={canEdit(incident) ? 'Open / Edit' : 'View'}
                    >
                      {canEdit(incident) ? '✏️' : '👁️'}
                    </button>
                    {canEdit(incident) && (
                      <button onClick={() => onDelete(incident)} className="btn-icon" title="Delete">🗑️</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default IncidentReportBoard;
